/**
 * PNG Metadata Utilities
 *
 * This module provides functionality for embedding metadata into PNG images
 * using tEXt chunks. PNG tEXt chunks store key-value pairs as uncompressed
 * Latin-1 text, which is ideal for storing QR code generation metadata.
 *
 * PNG Structure:
 * - 8-byte signature
 * - Chunks: each has 4-byte length, 4-byte type, data, 4-byte CRC
 * - tEXt chunks contain: keyword + null byte + text value
 *
 * Standard PNG keywords used:
 * - Title: Site name and bay identifier
 * - Description: The URL encoded in the QR code
 * - Author: User who generated the QR code
 * - Software: Application name and version
 *
 * Custom keywords (prefixed with 'X-'):
 * - X-SiteId, X-BayId, X-GeneratedAt, and all URL query parameters
 */

const APP_VERSION: string = import.meta.env.VITE_APP_VERSION || 'local-dev';

/**
 * Metadata to embed in the PNG file
 */
export interface PngMetadata {
  /** Site name for the Title field */
  readonly siteName: string;
  /** The full URL encoded in the QR code */
  readonly encodedUrl: string;
  /** User name/email for the Author field */
  readonly author: string;
  /** ISO timestamp for generation time */
  readonly generatedAt: string;
  /** Additional custom fields (query params, etc.) */
  readonly customFields: Readonly<Record<string, string>>;
}

/**
 * CRC32 lookup table for PNG chunk checksums
 */
const CRC_TABLE: readonly number[] = (() => {
  const table: number[] = [];
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c;
  }
  return table;
})();

/**
 * Calculates CRC32 checksum for PNG chunk validation
 *
 * @param data - The data to calculate CRC for (type + chunk data)
 * @returns The CRC32 checksum as an unsigned 32-bit integer
 */
function calculateCrc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * Creates a PNG tEXt chunk with the given keyword and value
 *
 * tEXt chunk format:
 * - Keyword (1-79 bytes, Latin-1)
 * - Null separator (1 byte)
 * - Text string (Latin-1)
 *
 * @param keyword - The metadata keyword (max 79 chars)
 * @param value - The metadata value
 * @returns Complete chunk including length, type, data, and CRC
 */
function createTextChunk(keyword: string, value: string): Uint8Array {
  // Truncate keyword to 79 chars max per PNG spec
  const safeKeyword = keyword.slice(0, 79);
  
  // Convert to Latin-1 bytes (ASCII subset)
  const keywordBytes = new TextEncoder().encode(safeKeyword);
  const valueBytes = new TextEncoder().encode(value);
  
  // Chunk data: keyword + null + value
  const chunkData = new Uint8Array(keywordBytes.length + 1 + valueBytes.length);
  chunkData.set(keywordBytes, 0);
  chunkData[keywordBytes.length] = 0; // null separator
  chunkData.set(valueBytes, keywordBytes.length + 1);
  
  // Chunk type: "tEXt"
  const chunkType = new Uint8Array([0x74, 0x45, 0x58, 0x74]); // tEXt
  
  // Calculate CRC over type + data
  const crcInput = new Uint8Array(chunkType.length + chunkData.length);
  crcInput.set(chunkType, 0);
  crcInput.set(chunkData, chunkType.length);
  const crc = calculateCrc32(crcInput);
  
  // Build complete chunk: length (4) + type (4) + data + crc (4)
  const chunk = new Uint8Array(4 + 4 + chunkData.length + 4);
  const view = new DataView(chunk.buffer);
  
  // Length (big-endian)
  view.setUint32(0, chunkData.length, false);
  
  // Type
  chunk.set(chunkType, 4);
  
  // Data
  chunk.set(chunkData, 8);
  
  // CRC (big-endian)
  view.setUint32(8 + chunkData.length, crc, false);
  
  return chunk;
}

/**
 * Parses query parameters from a URL into a record
 *
 * @param url - The full URL to parse
 * @returns Record of parameter names to values
 */
export function parseUrlParams(url: string): Record<string, string> {
  const params: Record<string, string> = {};
  
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });
  } catch {
    // If URL parsing fails, try manual extraction
    const queryStart = url.indexOf('?');
    if (queryStart !== -1) {
      const queryString = url.slice(queryStart + 1);
      const pairs = queryString.split('&');
      for (const pair of pairs) {
        const [key, value] = pair.split('=');
        if (key) {
          params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
        }
      }
    }
  }
  
  return params;
}

/**
 * Converts a data URL to a Uint8Array of PNG bytes
 *
 * @param dataUrl - The PNG data URL (data:image/png;base64,...)
 * @returns The raw PNG bytes
 */
function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(',')[1];
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Converts a Uint8Array back to a data URL
 *
 * @param bytes - The PNG bytes
 * @returns The data URL
 */
function bytesToDataUrl(bytes: Uint8Array): string {
  let binaryString = '';
  for (let i = 0; i < bytes.length; i++) {
    binaryString += String.fromCharCode(bytes[i]);
  }
  return `data:image/png;base64,${btoa(binaryString)}`;
}

/**
 * Finds the position of the IEND chunk in a PNG file
 *
 * IEND is always the last chunk and marks the end of the PNG.
 * We insert tEXt chunks just before IEND.
 *
 * @param pngBytes - The PNG file bytes
 * @returns The byte offset where IEND chunk starts, or -1 if not found
 */
function findIendPosition(pngBytes: Uint8Array): number {
  // PNG signature is 8 bytes, then chunks follow
  let pos = 8;
  
  // Need at least 12 bytes for a chunk (4 length + 4 type + 4 CRC, with 0 data)
  while (pos + 12 <= pngBytes.length) {
    const view = new DataView(pngBytes.buffer, pngBytes.byteOffset + pos);
    const length = view.getUint32(0, false);
    
    // Check if this is IEND (type at offset 4)
    if (
      pngBytes[pos + 4] === 0x49 && // I
      pngBytes[pos + 5] === 0x45 && // E
      pngBytes[pos + 6] === 0x4e && // N
      pngBytes[pos + 7] === 0x44    // D
    ) {
      return pos;
    }
    
    // Move to next chunk: length(4) + type(4) + data(length) + crc(4)
    pos += 4 + 4 + length + 4;
  }
  
  return -1;
}

/**
 * Embeds metadata into a PNG image using tEXt chunks
 *
 * This function takes a PNG data URL and returns a new data URL with
 * metadata embedded. The metadata is stored in tEXt chunks which are
 * inserted before the IEND chunk.
 *
 * Standard fields:
 * - Title: "{siteName} - {bayIdentifier}" or "{siteName} - Site QR"
 * - Description: The full encoded URL
 * - Author: User name/email
 * - Software: "Toptracer Go Admin {version}"
 *
 * Custom fields (X- prefixed):
 * - X-GeneratedAt: ISO timestamp
 * - X-{param}: Each URL query parameter
 *
 * @param dataUrl - The original PNG as a data URL
 * @param metadata - The metadata to embed
 * @returns New data URL with embedded metadata
 */
export function embedPngMetadata(
  dataUrl: string,
  metadata: PngMetadata
): string {
  const pngBytes = dataUrlToBytes(dataUrl);
  const iendPos = findIendPosition(pngBytes);
  
  if (iendPos === -1) {
    // Invalid PNG, return original
    console.warn('Could not find IEND chunk in PNG, returning original');
    return dataUrl;
  }
  
  // Build metadata chunks
  const chunks: Uint8Array[] = [];
  
  // Standard PNG keywords
  chunks.push(createTextChunk('Title', metadata.siteName));
  chunks.push(createTextChunk('Description', metadata.encodedUrl));
  chunks.push(createTextChunk('Author', metadata.author));
  chunks.push(createTextChunk('Software', `Toptracer Go Admin ${APP_VERSION}`));
  
  // Custom fields with X- prefix
  chunks.push(createTextChunk('X-GeneratedAt', metadata.generatedAt));
  
  // Add all custom fields (URL params, etc.)
  for (const [key, value] of Object.entries(metadata.customFields)) {
    chunks.push(createTextChunk(`X-${key}`, value));
  }
  
  // Calculate total size of new chunks
  const totalChunkSize = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  
  // Create new PNG with metadata inserted before IEND
  const newPng = new Uint8Array(pngBytes.length + totalChunkSize);
  
  // Copy everything before IEND
  newPng.set(pngBytes.subarray(0, iendPos), 0);
  
  // Insert metadata chunks
  let offset = iendPos;
  for (const chunk of chunks) {
    newPng.set(chunk, offset);
    offset += chunk.length;
  }
  
  // Copy IEND chunk
  newPng.set(pngBytes.subarray(iendPos), offset);
  
  return bytesToDataUrl(newPng);
}

/**
 * Builds PngMetadata from QR generation context
 *
 * @param siteName - The site name
 * @param encodedUrl - The URL encoded in the QR code
 * @param author - The user name/email
 * @returns Complete metadata object ready for embedding
 */
export function buildQrMetadata(
  siteName: string,
  encodedUrl: string,
  author: string
): PngMetadata {
  const urlParams = parseUrlParams(encodedUrl);
  
  return {
    siteName,
    encodedUrl,
    author,
    generatedAt: new Date().toISOString(),
    customFields: urlParams,
  };
}
